import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { MapPin, Clock, XCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

export default function TicketPage() {
  const { code } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/tickets/${code}`)
      .then(({ data }) => setTicket(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <div className="min-h-screen bg-ink text-bone flex flex-col" data-testid="ticket-page">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="px-6 md:px-12 h-[72px] flex items-center justify-between border-b border-bone/10">
        <Link to="/" className="flex items-baseline gap-2" data-testid="ticket-logo">
          <span className="font-hindi text-2xl text-saffron">सुमिरन</span>
          <span className="font-display text-sm tracking-[0.25em] text-bone">THE BAND SUMIRAN</span>
        </Link>
        <Link to="/" className="text-xs text-ash hover:text-saffron transition-colors" data-testid="ticket-back-home">Back to site</Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-14">
        {loading && <p className="text-ash">Loading ticket…</p>}
        {notFound && (
          <div className="text-center" data-testid="ticket-not-found">
            <XCircle size={44} className="mx-auto text-red-400" />
            <h1 className="mt-4 font-serif text-3xl text-bone">Ticket not found</h1>
            <p className="mt-2 text-sm text-ash">Check the link from your confirmation message.</p>
          </div>
        )}
        {ticket && (
          <div className="w-full max-w-md">
            <p className="text-center font-display text-xs tracking-[0.3em] text-saffron uppercase">Your Ticket</p>
            <div className="mt-6 border border-saffron/40" data-testid="ticket-pass">
              <div className="p-7 border-b border-dashed border-saffron/40 flex items-center justify-between gap-4">
                <div>
                  <p className="font-display text-[10px] tracking-[0.3em] text-ash uppercase">The Band Sumiran</p>
                  <p className="mt-2 font-serif text-2xl text-bone leading-snug" data-testid="ticket-event-name">{ticket.event_name}</p>
                  <p className="text-xs text-ash mt-1.5 flex items-center gap-1.5"><Clock size={11} className="text-saffron" /> {ticket.event_date} · {ticket.event_time}</p>
                  <p className="text-xs text-ash mt-1 flex items-center gap-1.5">
                    <MapPin size={11} className="text-saffron" />
                    {ticket.maps_url ? (
                      <a href={ticket.maps_url} target="_blank" rel="noreferrer" className="hover:text-saffron underline underline-offset-4 decoration-bone/20 transition-colors" data-testid="ticket-maps-link">{ticket.event_venue} — directions</a>
                    ) : ticket.event_venue}
                  </p>
                  <p className="text-xs text-saffron mt-2">{ticket.tier} · admits {ticket.admits}</p>
                </div>
                <div className="shrink-0 bg-[#F4EFE6] p-2" data-testid="ticket-qr">
                  <QRCodeCanvas value={ticket.ticket_code} size={104} bgColor="#F4EFE6" fgColor="#070605" level="M" />
                </div>
              </div>
              <div className="p-6 text-center">
                <p className="font-display text-[10px] tracking-[0.3em] text-ash uppercase">Ticket Code</p>
                <p className="mt-1.5 font-serif text-4xl tracking-[0.15em] text-gold" data-testid="ticket-code">{ticket.ticket_code}</p>
                {ticket.status === "cancelled" ? (
                  <p className="mt-3 text-xs text-red-400 font-medium" data-testid="ticket-status">THIS TICKET IS CANCELLED</p>
                ) : ticket.checked_in ? (
                  <p className="mt-3 text-xs text-green-400 flex items-center justify-center gap-1.5" data-testid="ticket-status"><CheckCircle2 size={13} /> Checked in</p>
                ) : (
                  <p className="mt-3 text-xs text-ash" data-testid="ticket-status">Show this at the entry gate</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
